import { useCallback, useEffect, useMemo, useState } from 'react';
import { FileCheck2, FileText, Printer, RefreshCw, RotateCcw, Search } from 'lucide-react';
import type { TenantSummary } from '@forq/contracts';
import type { ServiceBillingGateway, ServiceBillingLineKind, ServiceBillingProposal } from '@forq/service-billing-data';
import type { FinanceGateway, InvoiceLineKind } from '@forq/finance-data';
import { emptyCompanyProfile, type CompanyProfileGateway, type TenantCompanyProfile } from '@forq/platform-data';
import type { MasterdataGateway } from '@forq/masterdata-data';
import type { ServiceGateway } from '@forq/service-data';
import type { MachinesGateway } from '@forq/machines-data';
import { renderServiceBillingHtml, serviceDocumentLabels, type ServiceDocumentKind } from './serviceBillingDocument';

interface Props {
  readonly tenant: TenantSummary;
  readonly gateway: ServiceBillingGateway;
  readonly financeGateway: FinanceGateway;
  readonly companyGateway: CompanyProfileGateway;
  /** Für die Warengruppe je Artikelzeile (Stufe 3): löst die Artikelnummer der Materialzeile auf. */
  readonly masterdataGateway: MasterdataGateway;
  /** UVV-Automatik: Auftrag → Maschine auflösen. */
  readonly serviceGateway: ServiceGateway;
  /** UVV-Automatik: nächstes Prüfdatum der Maschine setzen. */
  readonly machinesGateway: MachinesGateway;
}

/** ISO-Datum (YYYY-MM-DD) um N Monate verschieben (für UVV: +12). */
function addMonthsIso(iso: string, months: number): string {
  const date = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(date.getTime())) return iso;
  date.setMonth(date.getMonth() + months);
  return date.toISOString().slice(0, 10);
}

const serviceLineKindToInvoice: Readonly<Record<ServiceBillingLineKind, InvoiceLineKind>> = {
  labour: 'labour', 'travel-time': 'travel', kilometres: 'travel', article: 'material', service: 'service', text: 'text',
};

function money(value: number) {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value);
}

export function FinanceServiceBillingPage({ tenant, gateway, financeGateway, companyGateway, masterdataGateway, serviceGateway, machinesGateway }: Props) {
  const [proposals, setProposals] = useState<readonly ServiceBillingProposal[]>([]);
  const [selected, setSelected] = useState<ServiceBillingProposal | null>(null);
  const [query, setQuery] = useState('');
  const [message, setMessage] = useState('');
  const [company, setCompany] = useState<TenantCompanyProfile>(emptyCompanyProfile);
  const [preview, setPreview] = useState<{ readonly proposal: ServiceBillingProposal; readonly kind: ServiceDocumentKind } | null>(null);

  const load = useCallback(async () => {
    const next = await gateway.listProposals(tenant.id, 'all');
    setProposals(next);
    setSelected((current) => current ? next.find((item) => item.id === current.id) ?? next[0] ?? null : next[0] ?? null);
  }, [gateway, tenant.id]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    let active = true;
    companyGateway.loadProfile(tenant.id).then((profile) => { if (active) setCompany(profile); }).catch(() => {});
    return () => { active = false; };
  }, [companyGateway, tenant.id]);

  const visible = proposals.filter((proposal) => !query.trim() || [proposal.serviceNumber, proposal.orderNumber, proposal.customerName, ...proposal.machineLabels].join(' ').toLocaleLowerCase('de').includes(query.toLocaleLowerCase('de')));

  const returnProposal = useCallback(async () => {
    if (!selected) return;
    const reason = window.prompt('Warum soll der Rechnungsvorschlag an Service zurückgegeben werden?', selected.returnedReason);
    if (!reason) return;
    await gateway.returnToService({ tenantId: tenant.id, proposalId: selected.id, reason });
    setMessage('Der Rechnungsvorschlag wurde mit Begründung an Service zurückgegeben.');
    await load();
  }, [gateway, load, selected, tenant.id]);

  // UVV-Automatik: Wurde eine als UVV gekennzeichnete Dienstleistung abgerechnet, wird an der Maschine
  // des Auftrags automatisch das nächste Prüfdatum auf +12 Monate gesetzt (letzte Prüfung = Servicedatum).
  // Die Servicezeile trägt keinen harten Stammbezug -> Best-Effort per exakter Nummer ODER exaktem Namen
  // (Angebotszeilen tragen oft keine Nummer). Nachrangig: Fehler blockieren die Rechnung nie.
  const advanceUvvIfBilled = useCallback(async (proposal: ServiceBillingProposal) => {
    if (!proposal.serviceJobId) return;
    const serviceLines = proposal.lines.filter((line) => line.kind === 'service');
    if (serviceLines.length === 0) return;
    let uvvBilled = false;
    for (const line of serviceLines) {
      const number = line.sourceNumber.trim();
      const description = line.description.trim();
      const needle = number || description;
      if (!needle) continue;
      try {
        const page = await masterdataGateway.searchItems({ tenantId: tenant.id, kind: 'service', query: needle, pageSize: 10 });
        const match = page.items.find((item) =>
          (number.length > 0 && item.number === number)
          || (description.length > 0 && item.name.trim().toLocaleLowerCase('de') === description.toLocaleLowerCase('de')));
        if (!match) continue;
        const detail = await masterdataGateway.getItem(tenant.id, match.id);
        if (detail.isUvv) { uvvBilled = true; break; }
      } catch { /* Auflösung ist Best-Effort. */ }
    }
    if (!uvvBilled) return;
    const job = await serviceGateway.getJob(tenant.id, proposal.serviceJobId);
    if (!job.machineId) return;
    await machinesGateway.setMachineInspection(tenant.id, job.machineId, addMonthsIso(proposal.serviceDate, 12), proposal.serviceDate);
  }, [masterdataGateway, serviceGateway, machinesGateway, tenant.id]);

  // Erzeugt eine ECHTE Rechnung in FORQ Finance (sourceType 'service') aus dem Vorschlag und ordnet
  // deren Nummer dem Vorschlag zu -- statt bloß eine Nummer von Hand einzutippen. Idempotent: existiert
  // schon eine Service-Rechnung zu diesem Auftrag, wird DIESE wiederverwendet (kein Doppelbeleg).
  const createInvoice = useCallback(async () => {
    if (!selected) return;
    try {
      const existing = (await financeGateway.listInvoices({ tenantId: tenant.id }))
        .find((invoice) => invoice.sourceType === 'service' && invoice.sourceNumber === selected.serviceNumber);
      // Warengruppe je Artikelzeile über die Artikelnummer auflösen (Servicezeilen tragen nur die
      // getippte Nummer, keinen harten Stammbezug -> Best-Effort per exaktem Nummern-Treffer; ohne
      // Treffer greift der kind-Fallback in der Auswertung). Arbeitszeit/Fahrt/DL bleiben ohne Warengruppe.
      const articleNumbers = [...new Set(selected.lines.filter((line) => line.kind === 'article' && line.sourceNumber.trim()).map((line) => line.sourceNumber.trim()))];
      const warengruppeByNumber = new Map<string, { id: string | null; name: string }>();
      await Promise.all(articleNumbers.map(async (number) => {
        try {
          const page = await masterdataGateway.searchItems({ tenantId: tenant.id, kind: 'article', query: number, pageSize: 5 });
          const match = page.items.find((item) => item.number === number);
          if (!match) return;
          // Die Suche führt die Warengruppe (noch) nicht mit -> Detail nachladen.
          const detail = await masterdataGateway.getItem(tenant.id, match.id);
          warengruppeByNumber.set(number, { id: detail.warengruppeId ?? null, name: detail.warengruppeName ?? '' });
        } catch { /* Warengruppe ist nachrangig. */ }
      }));
      const invoiceNumber = existing?.proposalNumber ?? (await financeGateway.createInvoice({
        tenantId: tenant.id,
        customerNumber: selected.customerNumber,
        customerName: selected.customerName,
        sourceType: 'service',
        sourceNumber: selected.serviceNumber,
        // Leistungsdatum (§ 14 UStG): der Tag, an dem der Service ausgeführt wurde.
        serviceDate: selected.serviceDate || null,
        lines: selected.lines.map((line) => {
          const warengruppe = line.kind === 'article' ? warengruppeByNumber.get(line.sourceNumber.trim()) : undefined;
          return {
            kind: serviceLineKindToInvoice[line.kind] ?? 'other',
            description: line.description,
            quantity: line.quantity,
            unit: line.unit,
            // Seit 09/2026 trägt die Rechnungszeile einen eigenen Rabatt -- der Notbehelf, ihn in den
            // Einzelpreis einzurechnen (und damit vor dem Kunden zu verstecken), entfällt.
            unitPrice: line.kind === 'text' ? 0 : line.unitPrice,
            discountPercent: line.kind === 'text' ? 0 : line.discountPercent,
            vatRate: line.vatRate,
            warengruppeId: warengruppe?.id ?? null,
            warengruppeName: warengruppe?.name ?? '',
          };
        }),
      })).proposalNumber;
      await gateway.markInvoiced({ tenantId: tenant.id, proposalId: selected.id, invoiceNumber });
      // UVV-Automatik nachrangig -- blockiert Rechnung/Meldung nicht.
      try { await advanceUvvIfBilled(selected); } catch { /* egal */ }
      setMessage(`Rechnung ${invoiceNumber} in FORQ Finance erzeugt und dem Servicevorschlag zugeordnet.`);
      await load();
    } catch (invoiceError) {
      setMessage(invoiceError instanceof Error ? invoiceError.message : 'Die Rechnung konnte nicht erzeugt werden.');
    }
  }, [advanceUvvIfBilled, financeGateway, gateway, masterdataGateway, load, selected, tenant.id]);

  return (
    <div className="finance-service-page">
      <header className="professional-module__header"><div><span>FORQ Finance · Abrechnung</span><h1>Rechnungsvorschläge Service</h1><p>Vom Service fachlich fertiggestellte Aufträge prüfen, zurückgeben oder als Rechnung übernehmen.</p></div><div className="professional-module__header-actions"><small>{tenant.shortName}</small><button type="button" onClick={() => void load()}><RefreshCw size={14} /> Aktualisieren</button></div></header>
      {message ? <div className="service-completion-message"><FileCheck2 size={15} /> {message}</div> : null}
      <div className="erp-status-strip"><article><span>Bereit</span><strong>{proposals.filter((item) => item.status === 'ready').length}</strong><small>zur Rechnungsprüfung</small></article><article><span>Zurückgegeben</span><strong>{proposals.filter((item) => item.status === 'returned').length}</strong><small>Korrektur durch Service</small></article><article><span>Fakturiert</span><strong>{proposals.filter((item) => item.status === 'invoiced').length}</strong><small>Rechnung erzeugt</small></article><article><span>Offenes Volumen</span><strong>{money(proposals.filter((item) => item.status === 'ready').reduce((sum, item) => sum + item.grossTotal, 0))}</strong><small>brutto</small></article></div>
      <div className="erp-toolbar"><label><Search size={14} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Serviceauftrag, Vorgang, Kunde oder Maschine" /></label><span>{visible.length} Vorschläge</span></div>
      <div className="finance-service-workspace">
        <section className="finance-service-list"><div className="finance-service-list__head"><span>Vorschlag</span><span>Kunde</span><span>Status</span><span>Betrag</span></div>{visible.map((proposal) => <button type="button" key={proposal.id} className={selected?.id === proposal.id ? 'is-selected' : ''} onClick={() => setSelected(proposal)}><span><strong>{proposal.serviceNumber}</strong><small>V{proposal.version} · Vorgang {proposal.orderNumber}</small></span><span><strong>{proposal.customerName}</strong><small>{proposal.machineLabels.join(', ')}</small></span><span>{proposal.status === 'ready' ? 'Bereit' : proposal.status === 'returned' ? 'Zurückgegeben' : proposal.status === 'invoiced' ? 'Fakturiert' : 'Entwurf'}</span><span>{money(proposal.grossTotal)}</span></button>)}</section>
        <aside className="finance-service-detail">{selected ? <><header><div><span>{selected.serviceNumber}</span><h2>{selected.customerName}</h2><p>{selected.invoiceAddress}</p></div><em>{selected.status}</em></header><dl><div><dt>Leistungsdatum</dt><dd>{selected.serviceDate}</dd></div><div><dt>Techniker</dt><dd>{selected.technicianNames.join(', ')}</dd></div><div><dt>Servicefreigabe</dt><dd>{selected.preparedBy}</dd></div><div><dt>Papier unterschrieben</dt><dd>{selected.customerSignatureOnPaper ? 'Ja' : 'Nein'}</dd></div></dl><section><h3>Ausgeführte Arbeiten</h3><p>{selected.workPerformed}</p>{selected.findings ? <><h3>Weitere Befunde</h3><p>{selected.findings}</p></> : null}</section><div className="finance-service-lines"><div><span>Position</span><span>Menge</span><span>Preis</span><span>Netto</span></div>{selected.lines.map((line) => <div key={line.id}><span><strong>{line.description}</strong><small>{line.sourceNumber}</small></span><span>{line.quantity} {line.unit}</span><span>{money(line.unitPrice)}</span><span>{money(line.netAmount)}</span></div>)}</div><div className="finance-service-totals"><span>Netto <strong>{money(selected.netTotal)}</strong></span><span>USt. <strong>{money(selected.vatTotal)}</strong></span><span>Brutto <strong>{money(selected.grossTotal)}</strong></span></div>{selected.returnedReason ? <div className="finance-service-returned"><strong>Rückgabegrund:</strong> {selected.returnedReason}</div> : null}<footer><button type="button" onClick={() => setPreview({ proposal: selected, kind: 'estimate' })}><FileText size={14} /> Kostenvoranschlag</button><button type="button" onClick={() => setPreview({ proposal: selected, kind: 'billing' })}><Printer size={14} /> Rechnungsvorschlag</button>{selected.status === 'ready' ? <><button type="button" onClick={() => void returnProposal()}><RotateCcw size={14} /> An Service zurückgeben</button><button type="button" className="is-primary" onClick={() => void createInvoice()}><FileCheck2 size={14} /> Rechnung erzeugen</button></> : null}</footer></> : <p>Keinen Rechnungsvorschlag ausgewählt.</p>}</aside>
      </div>
      {preview ? <ServiceDocumentModal proposal={preview.proposal} kind={preview.kind} company={company} onClose={() => setPreview(null)} /> : null}
    </div>
  );
}

function ServiceDocumentModal({ proposal, kind, company, onClose }: {
  readonly proposal: ServiceBillingProposal;
  readonly kind: ServiceDocumentKind;
  readonly company: TenantCompanyProfile;
  readonly onClose: () => void;
}) {
  const html = useMemo(() => renderServiceBillingHtml({
    kind,
    documentNumber: `${kind === 'estimate' ? 'KV' : 'RV'}-${proposal.serviceNumber}`,
    proposal,
    company,
    issuedIso: new Date().toISOString(),
  }), [proposal, kind, company]);
  return (
    <div className="orders-doc-modal" role="dialog" aria-modal="true" aria-label={serviceDocumentLabels[kind]} onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="orders-doc-modal__panel">
        <header><span>{serviceDocumentLabels[kind]} · {proposal.serviceNumber}</span><button type="button" className="orders-doc-modal__close" onClick={onClose}>Schließen</button></header>
        <iframe className="orders-doc-frame" title={serviceDocumentLabels[kind]} srcDoc={html} />
      </div>
    </div>
  );
}
